import { stateful } from "@escapio/shared/components/element-builder";
import { distance, center } from "../dom/touch";
import { trackPageOnce } from "../tracker/EventTracker";

const MAX_SCALE = 3;

class Zoomable extends HTMLElement {
	static getInitialState() {
		return {
			scale: 1,
			translate: [0, 0],
			pinchActive: false,
		};
	}

	constructor() {
		super();
		this.distance = null;
		this.coords = null;
		this.offset = 0;

		this.onTouchStart = this.onTouchStart.bind(this);
		this.onTouchMove = this.onTouchMove.bind(this);
		this.onTouchEnd = this.onTouchEnd.bind(this);
		this.onPictureChanged = this.onPictureChanged.bind(this);
	}

	connectedCallback() {
		this.addEventListener("touchstart", this.onTouchStart);
		this.addEventListener("touchmove", this.onTouchMove);
		this.addEventListener("touchend", this.onTouchEnd);
		document.addEventListener("AfterPictureChanged", this.onPictureChanged);

		this.initialRect = this.getBoundingClientRect();
		this.contentRect = null;
	}

	disconnectedCallback() {
		this.removeEventListener("touchstart", this.onTouchStart);
		this.removeEventListener("touchmove", this.onTouchMove);
		this.removeEventListener("touchend", this.onTouchEnd);
		document.removeEventListener("PictureChanged", this.onPictureChanged);
	}

	render() {
		const [x, y] = this.translate;
		this.style.transform = `translate(${x}px, ${y}px) scale(${this.scale})`;
	}

	onTouchStart({ touches }) {
		this.coords = center(touches);

		if (touches.length === 2) {
			this.pinchActive = true;
			this.distance = distance(...touches);
			this.dispatchEvent(new CustomEvent("ZoomStart"));
		}

		if (!this.contentRect) {
			this.contentRect = this.querySelector("img").getBoundingClientRect();
		}
	}

	onTouchMove(event) {
		if (!this.pinchActive) {
			return;
		}
		event.preventDefault();

		if (event.touches.length === 2) {
			const nextDistance = distance(...event.touches);
			const ratio = nextDistance / this.distance;
			this.scale = Math.min(MAX_SCALE, Math.max(1, this.scale * ratio));
			this.distance = nextDistance;
			event.stopPropagation();

			trackPageOnce(
				this.getAttribute("event-category"),
				this.getAttribute("event-action"),
				`${ratio > 1 ? "rein" : "raus"}${
					this.getAttribute("event-label-suffix") || ""
				}`,
			);
		}

		const [x, y] = center(event.touches);
		if (this.scale === 1) {
			this.translate = [0, 0];
		} else {
			const deltaX = x - this.coords[0];
			const deltaY = y - this.coords[1];

			const maxX =
				Math.max(
					0,
					this.contentRect.width * this.scale - this.initialRect.width,
				) / 2;
			const maxY =
				Math.max(
					0,
					this.contentRect.height * this.scale - this.initialRect.height,
				) / 2;

			this.translate = [
				Math.max(-maxX, Math.min(maxX, this.translate[0] + deltaX)),
				Math.max(-maxY, Math.min(maxY, this.translate[1] + deltaY)),
			];

			// Offset is how much we overpanned, if we did, bail out and let the event
			// bubble to let allow swiping.
			//
			// Offset can be accumulated and then "paid off", in the same gesture, so
			// that if we overpanned and let the parent swipe a bit, it can swipe back
			// into the original position.
			if (event.touches.length === 1) {
				const offset = Math.abs(this.translate[0] + deltaX) - maxX;
				if (this.offset > 0 || offset > 0) {
					this.offset = Math.max(0, offset);
					this.coords = [this.coords[0], y];
					return;
				}
			}

			// Hackish, but stopping propagation causes jerking in the swipe layer, as
			// it doesn't knows which were the last coordinates.
			event.inhibit = true;
		}

		this.coords = [x, y];
	}

	onTouchEnd(event) {
		// Recenter last coordinates when a touch is removed to prevent jerking.
		this.coords = center(event.touches);

		// Reset offset when the last touch is removed as it doesn't carry from
		// gesture to gesture.
		if (event.touches.length === event.changedTouches.length) {
			this.offset = 0;
		}

		this.pinchActive = this.pinchActive && event.touches.length;
		if (this.scale === 1) {
			this.dispatchEvent(new CustomEvent("ZoomEnd"));
		}
	}

	// Reset pan and zoom after swiping.
	onPictureChanged() {
		Object.assign(this, Zoomable.getInitialState());
		this.dispatchEvent(new CustomEvent("ZoomEnd"));
	}
}

customElements.define("esc-zoomable", stateful(Zoomable));
